<template>
    <Portal
        to="sidepanel"
    >
        <SidePanel
            :key="`review-new-versions-side-panel-${String(instance?.vnode?.key ?? '')}`"
            width="medium"
            name="Review Versions"
            class="review-new-versions-side-panel__sidepanel"
            :disable-transition="disableTransition"
            :is-dark-mode="isDarkMode"
            :class="{ 'review-new-versions-side-panel--full-height': showFullHeight }"
            @close="cancelVersionChange"
        >
            <Errors
                v-if="(!isLoading && errors.length)"
                :is-dark-mode="isDarkMode"
                :errors="errors"
                class="review-new-versions-side-panel__error"
            />
            <div
                class="review-new-versions-side-panel__label"
            >
                Which version {{ examName }} are you preparing for?
            </div>
            <ExamVersionCard
                v-for="(examVersion, index) in activeExamVersions"
                :key="examVersion.objectId"
                :exam="examVersion"
                :is-selected-version="Boolean(
                    selectedMajorVersion && examVersion.version.startsWith(`${selectedMajorVersion}.`)
                )"
                :reviewing-versions="true"
                :is-active-exam="currentExamVersion.version.split('.')[0] === examVersion.version.split('.')[0]"
                :is-latest-version="index === 0"
                :is-dark-mode="isDarkMode"
                @clickExamVersion="clickExamVersion"
            />
            <div
                v-if="activeExamVersions.length > 1"
                v-dark
                class="review-new-versions-side-panel__warning"
            >
                Each exam version has a separate set of questions, explanations, and references.
                Your stats are tied to an exam version.
                You can switch between versions without losing your progress.
            </div>
            <template #action>
                <PocketButton
                    :is-dark-mode="isDarkMode"
                    :disabled="selectedMajorVersion === currentExamVersion.version.split('.')[0]"
                    :is-loading="isChangingVersions"
                    @click="changeVersions"
                >
                    Change Version
                </PocketButton>
            </template>
        </SidePanel>
    </Portal>
</template>

<script setup lang="ts">
import { ref, computed, getCurrentInstance, onMounted } from 'vue'
import UIKit from '@pocketprep/ui-kit'
import type { Study } from '@pocketprep/types'
import type { ClientExam } from '@/store/bundle/state'
import { bundleModule } from '@/store/bundle/module'
import { userModule } from '@/store/user/module'
import { userExamMetadataModule } from '@/store/userExamMetadata/module'
import { examMetadataModule } from '@/store/examMetadata/module'
import ExamVersionCard from '@/components/Settings/EditExam/ExamVersionCard.vue'
import { toastModule } from '@/store/toast/module'
import { examVersionNativeAppName } from '@/utils'

const {
    SidePanel,
    Button: PocketButton,
    Errors,
} = UIKit

const instance = getCurrentInstance()

const props = withDefaults(defineProps<{
    showFullHeight?: boolean
    passedExamVersions: (Study.Class.ExamMetadataJSON | ClientExam)[]
    currentExamVersion: Study.Class.ExamMetadataJSON | ClientExam
    examMetadata: Study.Class.ExamMetadataJSON | ClientExam
    uem?: Study.Class.UserExamMetadataJSON | null
}>(), {
    showFullHeight: false,
    uem: null,
})

const emit = defineEmits<{
    close: []
    error: []
}>()

const isLoading = ref(true)
const errors = ref<string[]>([])
const disableTransition = ref(false)
const isChangingVersions = ref(false)
const selectedExamVersion = ref<string | undefined>('')
const selectedMajorVersion = ref<string | undefined>('')
const examId = ref('')
const examName = ref('')

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const activeExamVersions = computed(() => {
    return props.passedExamVersions.filter(exam => !examMetadataModule.getters.getIsExamDiscontinued(exam))
})

onMounted(async () => {
    await Promise.all([
        bundleModule.actions.fetchBundles(),
        examMetadataModule.actions.fetchExamMetadata(),
        userModule.actions.fetchUserData(),
        userExamMetadataModule.actions.fetchUserExamMetadata(),
    ])

    selectedMajorVersion.value = props.uem?.examVersion?.split('.')[0]
    examName.value = examVersionNativeAppName(props.examMetadata.nativeAppName)

    isLoading.value = false
})

const clickExamVersion = (exam: Study.Class.ExamMetadataJSON | ClientExam) => {
    examId.value = exam.objectId
    selectedExamVersion.value = exam.version
    selectedMajorVersion.value = exam.version.split('.')[0]
}

const changeVersions = async () => {
    if (!examId.value) {
        throw new Error('No selected exam version.')
    }

    if (!props.uem) {
        throw new Error('No current userExamMetadata.')
    }

    if (!selectedExamVersion.value) {
        throw new Error('No exam version selected.')
    }

    if (!selectedMajorVersion.value) {
        throw new Error('No major version selected.')
    }

    examMetadataModule.state.isSwitchingExams = true
    isChangingVersions.value = true

    if (props.uem && selectedMajorVersion.value !== props.currentExamVersion.version.split('.')[0]) {
        if (!props.examMetadata) {
            throw new Error('No exam metadata record found for version/guid.')
        }
        await userModule.actions.updateCurrentExam(examId.value)
    }
    emitClose()

    toastModule.actions.displayToast({
        title: `Updated ${props.examMetadata?.nativeAppName} version`,
    })
    isLoading.value = false
    isChangingVersions.value = false
    examMetadataModule.state.isSwitchingExams = false
}

const cancelVersionChange = () => {
    if (!props.uem) {
        throw new Error('No current userExamMetadata.')
    }
    emitClose()
}

const emitClose = () => {
    emit('close')
}
</script>

<style lang="scss" scoped>
.review-new-versions-side-panel {
    position: absolute;
    color: $brand-black;

    &--full-height {
        top: 0;
        height: 100%;

        @include breakpoint(black-bear) {
            height: calc(100% - 10px);
        }
    }

    &__modal {
        left: 50%;
        position: absolute;
        transform: translateX(-50%);
        top: 80px;
        width: 392px;
        padding: 35px 0 26px;
        text-align: center;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;

        @include breakpoint(black-bear) {
            width: 356px;
            height: auto;
            max-height: 638px;
            top: 10px;
        }
    }

    &__modal-title {
        font-size: 18px;
        line-height: 22px;
        font-weight: 600;
        width: 309px;
        margin: 0 auto 12px;
    }

    &__modal-message {
        font-size: 15px;
        line-height: 22px;
        color: $slate-03;
        margin-bottom: 29px;
        width: 309px;

        &--dark {
            color: $white;
        }
    }

    &__modal-buttons {
        display: flex;
        justify-content: center;
        align-items: center;
    }

    &__modal-button {
        margin: 0 6px;
    }

    &__error {
        margin: -12px;
        max-width: 396px;
    }

    &__label {
        font-size: 15px;
        line-height: 18px;
        font-weight: 600;
        margin-bottom: 18px;
    }

    &__warning {
        font-size: 15px;
        line-height: 18px;
        margin-bottom: 31px;
        color: $ash;

        &--dark {
            color: $fog;
        }
    }
}
</style>
